import { useRef, useState } from 'react'
import TurnstileWidget, { type TurnstileWidgetRef } from './TurnstileWidget'

interface FormData {
  name: string
  email: string
  phone: string
  service: string
  message: string
  turnstileToken: string
  privacyPolicyAccepted: boolean
}

interface FormState {
  status: 'idle' | 'submitting' | 'success' | 'error'
  message: string
}

const ContactForm = () => {
  const turnstileRef = useRef<TurnstileWidgetRef>(null)

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    turnstileToken: '',
    privacyPolicyAccepted: false
  })

  const [formState, setFormState] = useState<FormState>({
    status: 'idle',
    message: ''
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target

    // Handle checkboxes differently from text inputs
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.turnstileToken) {
      setFormState({
        status: 'error',
        message: 'Please complete the security verification.'
      })
      return
    }

    if (!formData.privacyPolicyAccepted) {
      setFormState({
        status: 'error',
        message: 'Please read and accept the privacy policy.'
      })
    }

    setFormState({ status: 'submitting', message: '' })

    const resetTurnstile = () => {
      turnstileRef.current?.reset()
      setFormData(prev => ({ ...prev, turnstileToken: '' }))
    }

    try {
      const apiUrl = import.meta.env.VITE_CONTACT_API_URL

      if (!apiUrl) {
        throw new Error('Contact API URL not configured')
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          formType: 'contact',
          timestamp: new Date().toISOString()
        })
      })

      let data: { message?: string; error?: string } | null = null
      try {
        data = await response.json()
      } catch {
        // Non-JSON response (e.g. CloudFront HTML error page)
      }

      if (response.ok && data?.message) {
        setFormState({
          status: 'success',
          message: 'Thank you! Your message has been sent. We aim to get back to you within 24 hours.'
        })
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
          turnstileToken: '',
          privacyPolicyAccepted: false
        })
      } else if (response.status === 403 || data?.error === 'turnstile_failed') {
        resetTurnstile()
        setFormState({
          status: 'error',
          message: 'The security check expired or was already used. Please complete the verification again and resubmit.'
        })
      } else {
        resetTurnstile()
        throw new Error(data?.error ?? 'Failed to submit form')
      }
    } catch (error) {
      console.error('Contact form submission error:', error)
      if (formState.status !== 'error') {
        resetTurnstile()
        setFormState({
          status: 'error',
          message: 'Sorry, there was an error sending your message. Please try again or call us directly.'
        })
      }
    }
  }

  const handleTurnstileSuccess = (token: string) => {
    setFormData(prev => ({
      ...prev,
      turnstileToken: token
    }))
  }

  const handleTurnstileError = () => {
    setFormData(prev => ({
      ...prev,
      turnstileToken: ''
    }))
  }

  const handleTurnstileExpire = () => {
    setFormData(prev => ({
      ...prev,
      turnstileToken: ''
    }))
  }

  const isSubmitDisabled = !formData.name || !formData.email || !formData.message || !formData.turnstileToken || !formData.privacyPolicyAccepted || formState.status === 'submitting'

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg">
      <h3 className="text-2xl font-semibold mb-6">Get in touch</h3>
      
      {formState.message && (
        <div
          className={`p-4 rounded-lg mb-6 ${
            formState.status === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
          role="alert"
        >
          {formState.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-dark-plum mb-2">
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-colors"
            placeholder="Enter your full name"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-dark-plum mb-2">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-colors"
            placeholder="Enter your email address"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-dark-plum mb-2">
            Phone Number
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-colors"
            placeholder="Enter your phone number"
          />
        </div>

        <div>
          <label htmlFor="service" className="block text-sm font-medium text-dark-plum mb-2">
            Service Interest
          </label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-colors"
          >
            <option value="">Select a service</option>
            <option value="solar">Solar Panels</option>
            <option value="heat-pump">Heat Pumps</option>
            <option value="ev-charging">EV Charging</option>
            <option value="multiple">Multiple Services</option>
            <option value="consultation">General Consultation</option>
          </select>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-dark-plum mb-2">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={4}
            value={formData.message}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-colors resize-vertical"
            placeholder="Tell us about your home and energy needs..."
          />
        </div>

        <div className="flex items-start">
          <input
            type="checkbox"
            id="privacyPolicyAccepted"
            name="privacyPolicyAccepted"
            checked={formData.privacyPolicyAccepted}
            onChange={handleInputChange}
            className="mt-1 h-4 w-4 text-primary-green focus:ring-primary-green border-gray-300 rounded"
          />
          <label htmlFor="privacyPolicyAccepted" className="select-none ms-2 text-sm text-gray-700">
            I have read and accept the usage of the information provided, according to the <a href="/privacy-policy" target="_blank" className="text-primary-green hover:text-primary-green-dark underline">privacy policy</a>. *
          </label>
        </div>

        <TurnstileWidget
          ref={turnstileRef}
          onSuccess={handleTurnstileSuccess}
          onError={handleTurnstileError}
          onExpire={handleTurnstileExpire}
          className="flex justify-center"
        />

        <button
          type="submit"
          disabled={isSubmitDisabled}
          className={`w-full py-4 px-6 rounded-lg font-medium text-white transition-all duration-200 ${
            isSubmitDisabled
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-primary-green hover:bg-primary-green-dark focus:outline-none focus:ring-2 focus:ring-primary-green focus:ring-opacity-50'
          }`}
        >
          {formState.status === 'submitting' ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </span>
          ) : (
            'Send Message'
          )}
        </button>

        <p className="text-sm text-neutral-taupe text-center">
          By submitting this form, you agree to be contacted by Gaia Powered.<br/>
          We respect your privacy and will never share your information.
        </p>
      </form>
    </div>
  )
}

export default ContactForm